import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, ChevronLeft, Save, Loader2, Home, Search, Edit2 } from "lucide-react";
import { INVENTORY_CATEGORIES, INVENTORY_STATUSES, newInventoryItem, emptyInventoryCompartment } from "../../lib/vault/schemas";

/**
 * InventoryCompartment — household property inventory.
 * All data stays encrypted on the device. Serial numbers, prices and
 * exact valuations never leave the vault (in particular they never enter
 * the TravelerPulse trip bridge — that only takes nickname + category).
 */
export default function InventoryCompartment({ vault, compartmentId, onBack }) {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    vault.load(compartmentId).then((d) => setData(d || emptyInventoryCompartment())).catch(() => setData(emptyInventoryCompartment()));
  }, [vault, compartmentId]);

  const save = async () => {
    setSaving(true);
    try { await vault.save(compartmentId, data); toast.success("Saved"); setDirty(false); }
    catch (e) { toast.error(e.message || "Could not save"); }
    setSaving(false);
  };

  const addItem = () => {
    const item = newInventoryItem();
    setData((d) => ({ ...d, items: [...d.items, item] }));
    setDirty(true);
    setEditingId(item.id);
  };

  const updateItem = (id, updater) => {
    setData((d) => ({ ...d, items: d.items.map((it) => it.id === id ? updater(it) : it) }));
    setDirty(true);
  };

  const removeItem = (id) => {
    if (!window.confirm("Remove this item permanently?")) return;
    setData((d) => ({ ...d, items: d.items.filter((it) => it.id !== id) }));
    setDirty(true);
    if (editingId === id) setEditingId(null);
  };

  if (!data) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>;

  const inp = "mt-1 w-full border border-slate-300 focus:border-emerald-500 px-3 py-2 text-sm rounded";
  const filtered = data.items.filter((it) =>
    !q || it.name.toLowerCase().includes(q.toLowerCase()) ||
    (it.brand || "").toLowerCase().includes(q.toLowerCase()) ||
    (it.category || "").toLowerCase().includes(q.toLowerCase())
  );
  const editing = editingId ? data.items.find((it) => it.id === editingId) : null;

  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="inventory-compartment">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
        <button onClick={save} disabled={!dirty || saving} data-testid="inventory-save"
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-700 rounded inline-flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-100 rounded p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-600 grid place-items-center"><Boxes className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="font-serif font-900 text-2xl">Household Inventory</h1>
            <p className="text-[11px] text-slate-500">Encrypted · {data.items.length} items · replacement records for insurance</p>
          </div>
        </div>
      </div>

      {editing ? (
        <div className="border border-emerald-300 bg-emerald-50/30 rounded p-4 space-y-3" data-testid={`inventory-editor-${editing.id}`}>
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-800 text-sm">Item details</h3>
            <button onClick={() => setEditingId(null)} className="text-xs text-emerald-700 hover:underline">Close editor</button>
          </div>
          <label className="block text-sm"><span className="font-600">Name</span>
            <input autoFocus value={editing.name} onChange={(e) => updateItem(editing.id, (x) => ({...x, name: e.target.value}))} className={inp} data-testid="inventory-edit-name" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Category</span>
              <select value={editing.category} onChange={(e) => updateItem(editing.id, (x) => ({...x, category: e.target.value}))} className={inp}>
                {INVENTORY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block text-sm"><span className="font-600">Status</span>
              <select value={editing.status} onChange={(e) => updateItem(editing.id, (x) => ({...x, status: e.target.value}))} className={inp}>
                {INVENTORY_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Brand</span>
              <input value={editing.brand} onChange={(e) => updateItem(editing.id, (x) => ({...x, brand: e.target.value}))} className={inp} />
            </label>
            <label className="block text-sm"><span className="font-600">Model</span>
              <input value={editing.model} onChange={(e) => updateItem(editing.id, (x) => ({...x, model: e.target.value}))} className={inp} />
            </label>
          </div>
          <label className="block text-sm"><span className="font-600">Serial number</span>
            <input value={editing.serialNumber} onChange={(e) => updateItem(editing.id, (x) => ({...x, serialNumber: e.target.value}))} className={inp}
              placeholder="Kept on-device only" data-testid="inventory-edit-serial" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-sm col-span-1"><span className="font-600">Currency</span>
              <input value={editing.currency} onChange={(e) => updateItem(editing.id, (x) => ({...x, currency: e.target.value}))} className={inp} maxLength={4} />
            </label>
            <label className="block text-sm col-span-1"><span className="font-600">Purchase price</span>
              <input type="number" value={editing.purchasePrice ?? ""} onChange={(e) => updateItem(editing.id, (x) => ({...x, purchasePrice: e.target.value === "" ? null : Number(e.target.value)}))} className={inp} />
            </label>
            <label className="block text-sm col-span-1"><span className="font-600">Replacement value</span>
              <input type="number" value={editing.estimatedReplacementValue} onChange={(e) => updateItem(editing.id, (x) => ({...x, estimatedReplacementValue: Number(e.target.value) || 0}))} className={inp} data-testid="inventory-edit-replacement" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Purchase date</span>
              <input type="date" value={editing.purchaseDate || ""} onChange={(e) => updateItem(editing.id, (x) => ({...x, purchaseDate: e.target.value}))} className={inp} />
            </label>
            <label className="block text-sm"><span className="font-600">Warranty expires</span>
              <input type="date" value={editing.warrantyExpiration || ""} onChange={(e) => updateItem(editing.id, (x) => ({...x, warrantyExpiration: e.target.value}))} className={inp} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Location in home</span>
              <input value={editing.locationInHome} onChange={(e) => updateItem(editing.id, (x) => ({...x, locationInHome: e.target.value}))} className={inp}
                placeholder="e.g. Home Office · Top shelf" list="inventory-rooms" />
              <datalist id="inventory-rooms">{data.rooms.map((r) => <option key={r} value={r} />)}</datalist>
            </label>
            <label className="block text-sm"><span className="font-600">Owner</span>
              <input value={editing.owner} onChange={(e) => updateItem(editing.id, (x) => ({...x, owner: e.target.value}))} className={inp}
                placeholder="Family member" list="inventory-owners" />
              <datalist id="inventory-owners">{data.owners.map((o) => <option key={o} value={o} />)}</datalist>
            </label>
          </div>
          <label className="block text-sm"><span className="font-600">Notes</span>
            <textarea rows={2} value={editing.notes} onChange={(e) => updateItem(editing.id, (x) => ({...x, notes: e.target.value}))} className={inp} />
          </label>
          <div className="text-right">
            <button onClick={() => removeItem(editing.id)} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete item</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search inventory…"
                data-testid="inventory-search" className="w-full border border-slate-300 pl-10 pr-3 py-2 text-sm rounded" />
            </div>
            <button onClick={addItem} data-testid="inventory-add"
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-700 rounded inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add item
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded p-8 text-center" data-testid="inventory-empty">
              <Boxes className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500 mb-3">{data.items.length === 0 ? "Track valuables for insurance and travel-with-me packing." : "No items match your search."}</p>
              {data.items.length === 0 && <button onClick={addItem} className="px-4 py-2 bg-emerald-600 text-white text-sm font-700 rounded inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add your first item</button>}
            </div>
          ) : (
            <div className="border border-slate-200 rounded divide-y divide-slate-100 bg-white" data-testid="inventory-list">
              {filtered.map((it) => (
                <div key={it.id} className="p-3 flex items-center justify-between gap-3" data-testid={`inventory-row-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-700 text-slate-900 truncate">{it.name || <em className="text-slate-400">Untitled</em>}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {it.category} · {[it.brand, it.model].filter(Boolean).join(" ") || "no brand"}{it.locationInHome && ` · ${it.locationInHome}`}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {it.currency} {Number(it.estimatedReplacementValue || 0).toLocaleString()}
                      {it.status !== "active" && <span className="ml-2 uppercase font-800 tracking-wider text-rose-700">{it.status}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setEditingId(it.id)} data-testid={`inventory-edit-${it.id}`} className="p-1.5 text-slate-400 hover:text-emerald-700"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => removeItem(it.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="mt-4 text-[10px] text-slate-500 flex items-start gap-1">
        <Home className="w-3 h-3 shrink-0 mt-0.5" /> Serial numbers, prices and photos are encrypted and never leave this device. The TravelerPulse trip bridge only carries opaque nicknames.
      </div>
    </div>
  );
}
